'use client';

import Alert from '@legacy/styled-components/components/Alert';
import Container from '@legacy/styled-components/components/Container';
import Loader from '@legacy/styled-components/components/Loader';
import { PageSection } from '@legacy/styled-components/components/PageSection';
import PageHeader from '@legacy/styled-components/structures/PageHeader';
import Cookies from 'cookies-js';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const FailedFetchDynamicImportedModuleChrome = 'Failed to fetch dynamically imported module:';
const FailedFetchDynamicImportedModuleIos = 'Importing a module script failed';

const isDynamicImportModuleError = (error: Error) => {
  const message = error.message;
  if (
    message?.startsWith(FailedFetchDynamicImportedModuleChrome) ||
    message?.startsWith(FailedFetchDynamicImportedModuleIos)
  ) {
    const retries = Number.parseInt(Cookies.get('w-retries') || '0', 10) || 0;
    return { error: true, canReload: retries < 1, currentRetries: retries };
  }
  return { error: false };
};

interface ErrorPageProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function ErrorPage({ error, reset }: ErrorPageProps) {
  const tActivity = useTranslation('activity').t;

  const tOther = useTranslation('other').t;

  // biome-ignore lint/suspicious/noConsole: Log error for debugging purposes
  console.log(error);

  const dynamicError = isDynamicImportModuleError(error);

  useEffect(() => {
    if (dynamicError.error && dynamicError.canReload) {
      Cookies.set('w-retries', (dynamicError.currentRetries + 1).toString(), {
        expires: new Date(new Date().getTime() + 10 * 1000),
      });
      window.location.reload();
    }
  }, [dynamicError]);

  return (
    <Container>
      <PageHeader
        heading={tOther('pageError.heading')}
        preamble={<p>{tOther('pageError.description')}</p>}
        breadcrumbs={[
          {
            displayName: tActivity('activity'),
            link: {
              to: '/',
              title: tActivity('activity'),
            },
          },
          {
            displayName: tOther('pageError.heading'),
          },
        ]}
      />
      <PageSection>
        {dynamicError.error && dynamicError.canReload ? (
          <Loader />
        ) : (
          <Alert
            tone="error"
            heading={tOther('pageError.alertHeading') || undefined}
            message={error.message || tOther('pageError.alertMessageUnknown')}
          />
        )}
      </PageSection>
    </Container>
  );
}
